/**
 * "Jump to the condition that matched" — the hand-off from a block's test bar to its own editor,
 * which are two separately-mounted trees with no prop path between them.
 *
 * Two paths, because the editor may or may not already be mounted when the jump is requested:
 * - Already open (the common case — a teacher testing with the editor panel visible alongside):
 *   a live subscriber is called directly for an immediate update. A mount-time-only hand-off would
 *   silently never take effect here, since nothing re-runs a `useState` lazy initializer on an
 *   already-mounted component.
 * - Not yet open: the index parks in `pending`, consumed once at the editor's own mount and
 *   cleared immediately after, so it never overrides a teacher's own subsequent navigation.
 *
 * Deliberately NOT automatic on every reroll — only when the teacher clicks the jump action.
 *
 * All six branching blocks had their own copy of both maps and both halves of the protocol. One
 * registry is safe because the keys are block ids, which are unique across a task, and it means a
 * change to the hand-off rule cannot land in five files and miss the sixth.
 */
import { useEffect, useState } from 'react';

const listeners = new Map<string, (index: number) => void>();
const pending = new Map<string, number>();

/** Called from the test surface. */
export function requestBranchJump(blockId: string, index: number) {
  const listener = listeners.get(blockId);
  if (listener) { listener(index); return; }
  pending.set(blockId, index);
}

/**
 * The editor side: owns the active branch index, seeds it from any jump requested before this
 * mount, and subscribes for jumps requested while it is open. Returns the usual state pair.
 */
export function useBranchJumpTarget(blockId: string): [number, React.Dispatch<React.SetStateAction<number>>] {
  const [index, setIndex] = useState(() => {
    const queued = pending.get(blockId);
    if (queued === undefined) return 0;
    pending.delete(blockId);
    return queued;
  });
  useEffect(() => {
    listeners.set(blockId, setIndex);
    return () => { listeners.delete(blockId); };
  }, [blockId]);
  return [index, setIndex];
}
